import { motion } from 'framer-motion';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import formatNumber from '../../utils/formatNumber';

const UPGRADE_COLORS = {
  Basic: '#6b7280',
  Island: '#0088FE',
  Ocean: '#00C49F',
  Jungle: '#22c55e',
  Arctic: '#60a5fa',
  Wasteland: '#84C341',
  Monsters: '#a855f7',
  Biodome: '#14b8a6',
  'Space Station': '#8b5cf6',
  'Acid Overload': '#FFBB28',
};

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const color = payload[0].payload.color || '#84C341';
    return (
      <div
        className='px-4 py-3 rounded-lg'
        style={{
          background: 'rgba(23,23,23,0.95)',
          border: `1px solid ${color}`,
          boxShadow: `0 0 20px ${color}40`,
        }}
      >
        <p className='text-neutral-200 font-bold'>{label}</p>
        <p style={{ color }} className='text-lg font-black'>
          {formatNumber(payload[0].value)} cities
        </p>
      </div>
    );
  }
  return null;
}

function RarityHistogram({ rarityDistribution }) {
  if (!rarityDistribution || rarityDistribution.length === 0) return null;

  const maxCount = Math.max(...rarityDistribution.map((b) => b.count));
  const data = rarityDistribution.map((bucket, index) => ({
    ...bucket,
    range: `${bucket.min}-${bucket.max}`,
    color: `hsl(${120 + index * 24}, 70%, 50%)`,
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className='rounded-xl p-4 lg:p-5 flex flex-col'
      style={{
        background: 'linear-gradient(135deg, rgba(38,38,38,0.6) 0%, rgba(23,23,23,0.8) 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <h3 className='text-base lg:text-lg font-bold text-neutral-200 mb-3 lg:mb-4 flex items-center gap-2'>
        <span className='w-2 h-2 rounded-full bg-[#84C341]' />
        Rarity Score Distribution
      </h3>

      <div className='flex-1 min-h-[180px] lg:min-h-[200px]'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={data} barCategoryGap='15%'>
            <XAxis
              dataKey='range'
              tick={{ fill: '#a3a3a3', fontSize: 10 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              angle={-45}
              textAnchor='end'
              height={50}
            />
            <YAxis hide />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <Bar dataKey='count' radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.8} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className='mt-4 flex items-center justify-between text-sm text-neutral-500'>
        <span>Lower Rarity</span>
        <div className='h-1 flex-1 mx-4 rounded-full bg-gradient-to-r from-[#22c55e] via-[#0088FE] to-[#a855f7]' />
        <span>Higher Rarity</span>
      </div>
    </motion.div>
  );
}

function UpgradesTierChart({ upgradesByTier }) {
  if (!upgradesByTier || upgradesByTier.length === 0) return null;

  const data = upgradesByTier
    .filter((u) => u.count > 0)
    .map((upgrade) => ({
      ...upgrade,
      color: UPGRADE_COLORS[upgrade.name] || '#84C341',
    }));

  if (data.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className='rounded-xl p-5'
        style={{
          background: 'linear-gradient(135deg, rgba(38,38,38,0.6) 0%, rgba(23,23,23,0.8) 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <h3 className='text-base lg:text-lg font-bold text-neutral-200 mb-3 lg:mb-4 flex items-center gap-2'>
          <span className='w-2 h-2 rounded-full bg-[#FF8042]' />
          City Upgrades
        </h3>
        <p className='text-neutral-500 text-center py-6 lg:py-8'>No upgraded cities yet</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className='rounded-xl p-4 lg:p-5 flex flex-col'
      style={{
        background: 'linear-gradient(135deg, rgba(38,38,38,0.6) 0%, rgba(23,23,23,0.8) 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <h3 className='text-base lg:text-lg font-bold text-neutral-200 mb-3 lg:mb-4 flex items-center gap-2'>
        <span className='w-2 h-2 rounded-full bg-[#FF8042]' />
        City Upgrades
      </h3>

      <div className='flex-1 min-h-[180px] lg:min-h-[200px]'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={data} layout='vertical' barCategoryGap='15%'>
            <XAxis type='number' hide />
            <YAxis
              type='category'
              dataKey='name'
              tick={{ fill: '#a3a3a3', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={110}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <Bar dataKey='count' radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.8} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}

function CapacityStats({ cityStats, totalCities }) {
  const stats = [
    {
      label: 'Total Cities',
      value: formatNumber(totalCities),
      color: '#0088FE',
    },
    {
      label: 'Total Capacity',
      value: formatNumber(cityStats.totalCapacity),
      color: '#84C341',
    },
    {
      label: 'Current Population',
      value: formatNumber(cityStats.totalPopulation),
      color: '#FFBB28',
    },
    {
      label: 'Available Space',
      value: formatNumber(cityStats.totalCapacity - cityStats.totalPopulation),
      color: '#00C49F',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className='rounded-xl p-4 lg:p-5 flex flex-col'
      style={{
        background: 'linear-gradient(135deg, rgba(38,38,38,0.6) 0%, rgba(23,23,23,0.8) 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <h3 className='text-base lg:text-lg font-bold text-neutral-200 mb-3 lg:mb-4 flex items-center gap-2'>
        <span className='w-2 h-2 rounded-full bg-[#00C49F]' />
        Capacity Overview
      </h3>

      <div className='flex-1 flex flex-col justify-center'>
        <div className='grid grid-cols-2 gap-2 lg:gap-4'>
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
              className='text-center p-2 lg:p-3 rounded-lg'
              style={{
                background: `${stat.color}10`,
                border: `1px solid ${stat.color}30`,
              }}
            >
              <p className='text-[10px] lg:text-xs text-neutral-500 uppercase tracking-wide'>{stat.label}</p>
              <p className='text-lg lg:text-xl font-black mt-1' style={{ color: stat.color }}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        <div className='mt-4'>
          <div className='flex justify-between text-sm mb-2'>
            <span className='text-neutral-500'>Capacity Utilization</span>
            <span className='text-neutral-300 font-semibold'>{cityStats.avgSaturation}%</span>
          </div>
          <div className='h-3 rounded-full overflow-hidden' style={{ background: 'rgba(38,38,38,0.8)' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${cityStats.avgSaturation}%` }}
              transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className='h-full rounded-full'
              style={{
                background: 'linear-gradient(90deg, #84C341 0%, #FFBB28 50%, #FF8042 100%)',
                boxShadow: '0 0 10px rgba(132, 195, 65, 0.5)',
              }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function CensusCityStats({ cityStats, totalCities }) {
  if (!cityStats) return null;

  return (
    <section className='py-12'>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className='text-center mb-8'>
        <h2 className='text-xl lg:text-2xl font-black text-neutral-200 tracking-tight uppercase'>City Statistics</h2>
        <p className='text-neutral-500 mt-2 text-sm lg:text-base'>Rarity distribution, upgrades, and capacity</p>
        <div className='mt-3 mx-auto w-16 h-1 rounded-full bg-gradient-to-r from-[#84C341] via-[#0088FE] to-[#FF8042]' />
      </motion.div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch'>
        <RarityHistogram rarityDistribution={cityStats.rarityDistribution} />
        <UpgradesTierChart upgradesByTier={cityStats.upgradesByTier} />
        <CapacityStats cityStats={cityStats} totalCities={totalCities} />
      </div>
    </section>
  );
}
